import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, Loader2, Plus, Search, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { can } from "../lib/permissions";
import { useAuth } from "../context/AuthContext";
import { useRefData } from "../context/RefDataContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ClientFormWarning } from "./ClientCompleteness";

const MAX_SUGGESTIONS = 8;
const normalize = (text) => String(text || "").toLocaleLowerCase("it-IT").normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

// Search an existing customer by typing, or create one on the spot with the
// essentials (type and name; phone and email optional). The full profile is
// completed later in Clienti. Creating needs the same rights as in Clienti.
// A chosen customer whose profile lacks data gets a warning that does not block.
export default function ClientPicker({ id, value, onChange, onlyClients = false, disabled = false, invalid = false, testId }) {
  const { user } = useAuth();
  const refData = useRefData();
  const listId = useId();
  const [created, setCreated] = useState([]);
  const people = useMemo(() => {
    const known = (refData?.assignees || []).filter((a) => !onlyClients || a.type === "cliente");
    return [...known, ...created.filter((c) => !known.some((k) => k.id === c.id))];
  }, [refData, onlyClients, created]);
  const selected = people.find((p) => p.id === value);
  const [text, setText] = useState(selected?.name || "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [draft, setDraft] = useState(null); // {customer_kind, name, phone, email}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const box = useRef(null);

  // The field shows the chosen customer, also when the value changes from outside.
  useEffect(() => { if (!open && !draft) setText(selected?.name || ""); }, [selected?.name, open, draft]);
  useEffect(() => {
    const close = (event) => { if (box.current && !box.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const query = normalize(text);
  const matches = people.filter((p) => !query || normalize(p.name).includes(query)).slice(0, MAX_SUGGESTIONS);
  const exact = people.some((p) => normalize(p.name) === query);
  const mayCreate = can(user, "customers.manage") && can(user, "customers.private");
  const singleSite = user?.active_site_id && user.active_site_id !== "all";
  const offerCreate = mayCreate && query && !exact;
  const options = [...matches.map((p) => ({ kind: "pick", person: p })), ...(offerCreate ? [{ kind: "create" }] : [])];

  const pick = (person) => { onChange(person.id, person); setText(person.name); setOpen(false); };
  const startCreate = () => {
    setOpen(false); setError("");
    setDraft({ customer_kind: "organizzazione", name: text.trim(), phone: "", email: "" });
  };
  const choose = (option) => option.kind === "create" ? startCreate() : pick(option.person);
  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (event.key === "Enter" && open && options[active]) { event.preventDefault(); choose(options[active]); }
    else if (event.key === "Escape") { setOpen(false); setText(selected?.name || ""); }
  };

  const create = async () => {
    setBusy(true); setError("");
    try {
      const payload = { customer_kind: draft.customer_kind, name: draft.name.trim() };
      if (draft.phone.trim()) payload.phone = draft.phone.trim();
      if (draft.email.trim()) payload.email = draft.email.trim();
      const { data } = await api.post("/clients", payload);
      setCreated((rows) => [...rows, data]);
      onChange(data.id, data);
      setText(data.name);
      setDraft(null);
      refData?.reload?.();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (draft) {
    return (
      <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="client-quick-create">
        <p className="text-sm font-semibold">Nuovo cliente</p>
        <div className="flex gap-2" role="radiogroup" aria-label="Tipo di cliente">
          {[["organizzazione", "Azienda"], ["persona", "Persona"]].map(([kind, label]) => (
            <Button key={kind} type="button" size="sm" variant={draft.customer_kind === kind ? "default" : "outline"} role="radio"
              aria-checked={draft.customer_kind === kind} onClick={() => setDraft({ ...draft, customer_kind: kind })}>{label}</Button>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="sm:col-span-3"><Label htmlFor={`${id}-name`} className="mb-1 block text-xs">{draft.customer_kind === "persona" ? "Nome e cognome" : "Ragione sociale"} <span className="text-destructive" aria-hidden="true">*</span></Label>
            <Input id={`${id}-name`} value={draft.name} maxLength={180} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="client-quick-name" /></div>
          <div><Label htmlFor={`${id}-phone`} className="mb-1 block text-xs">Telefono</Label>
            <Input id={`${id}-phone`} type="tel" value={draft.phone} maxLength={60} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label htmlFor={`${id}-email`} className="mb-1 block text-xs">Email</Label>
            <Input id={`${id}-email`} type="email" value={draft.email} maxLength={254} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></div>
        </div>
        {!singleSite && <p className="text-xs text-muted-foreground">Scegli una sede in alto: il cliente appartiene a una sede.</p>}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <p className="text-xs text-muted-foreground">Il resto della scheda (documenti, persone autorizzate, dati fiscali) si completa in Clienti.</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setDraft(null)}><X aria-hidden="true" />Annulla</Button>
          <Button type="button" size="sm" disabled={busy || !draft.name.trim() || !singleSite} onClick={create} data-testid="client-quick-save">
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}Crea e usa
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
    <div className="relative" ref={box}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input
        id={id}
        value={text}
        disabled={disabled}
        autoComplete="off"
        placeholder="Cerca o scrivi un nuovo cliente…"
        className="pl-9"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
        onFocus={() => { setOpen(true); setActive(0); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0); if (value) onChange(""); }}
        onKeyDown={onKeyDown}
        data-testid={testId}
      />
      {open && !disabled && (
        <ul id={listId} role="listbox" className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          {options.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{query ? "Nessun cliente trovato" : "Nessun cliente registrato"}</li>}
          {options.map((option, index) => (
            <li key={option.kind === "create" ? "__create" : option.person.id} id={`${listId}-${index}`} role="option" aria-selected={index === active}
              onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(option)}
              className={`flex min-h-10 cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm ${index === active ? "bg-accent text-accent-foreground" : ""} ${option.kind === "create" ? "border-t border-border font-medium text-primary" : ""}`}
              data-testid={option.kind === "create" ? "client-create-option" : `client-option-${option.person.id}`}>
              {option.kind === "create"
                ? <><Plus className="h-4 w-4" aria-hidden="true" />Nuovo cliente «{text.trim()}»</>
                : <><span className="truncate">{option.person.name}</span>{option.person.id === value && <Check className="ml-auto h-4 w-4" aria-hidden="true" />}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
    {value && <ClientFormWarning client={selected} />}
    </div>
  );
}
